import localForageHelper from '@/helpers/localforage.helper'
import HubConfig from '@/interfaces/hub-config.interface'
import onEnvironmentChange from '@/observables/environment.observable'
import { SelectBank, SelectOperationCode } from '@interfaces/bank.interface'
import type Environment from '@interfaces/environment.interface'
import State from '@interfaces/state.interface'
import ZipCodeResponse from '@interfaces/zipcode.interface'
import LogService from '@services/log/log.service'
import MainService from '@services/main/main.service'
import type { AxiosResponse } from 'axios'
import { take } from 'rxjs/operators'

export default class BatmanService extends MainService {
  url: string
  logServce = new LogService()

  constructor(batmanUrl?: string) {
    super()
    if (!batmanUrl) {
      onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
        this.url = environment?.squidApis?.batman
      })
    } else {
      this.url = batmanUrl
    }
    this.logServce.init('Batman Service')
  }

  async getCnpjInfos(cnpj: string) {
    if (!cnpj) {
      throw new Error('CNPJ is required')
    }
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/hub/cnpj/${cnpj}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logServce.error({
          message: 'Error on get CNPJ infos from ' + cnpj,
          error,
        })
        return error.response
      })
  }

  async getBanks(): Promise<{ banks: Array<SelectBank>; operationCode: Array<SelectOperationCode> }> {
    const banksResponse = await this.http
      .get(`${this.url}/banks`)
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logServce.error({
          message: 'Error on get banks',
          error,
        })
        throw error.response
      })

    return banksResponse?.banks ? banksResponse : { banks: [], operationCode: [] }
  }

  async getStates(): Promise<Array<State>> {
    const states: Array<State> = (await localForageHelper.getItem('states')) || []
    if (!states?.length) {
      const statesResponse = await this.http
        .get(`${this.url}/states`)
        .then((r: AxiosResponse) => r.data?.states || [])
        .catch((error: any) => {
          this.logServce.error({
            message: 'Error on get states',
            error,
          })
          return error.response
        })
      if (statesResponse?.length) {
        await localForageHelper.setItem('states', statesResponse)
      }
      return statesResponse?.length ? statesResponse : []
    }
    return states
  }

  getCities(state: string): Promise<Array<string>> {
    if (!state) {
      throw new Error('state is required')
    }
    return this.http
      .get(`${this.url}/cities`, {
        params: {
          q: state,
        },
      })
      .then((r: AxiosResponse) => r.data?.data || [])
      .catch((error: any) => {
        this.logServce.error({
          message: 'Error on get cities from state ' + state,
          error,
        })
        return error.response
      })
  }

  async getAddressByZipcode(zipcode: string): Promise<ZipCodeResponse> {
    if (!zipcode) {
      throw new Error('zipcode is required')
    }
    return this.http
      .get(`${this.url}/address/${zipcode}`)
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logServce.error({
          message: 'Error on get zipcode infos from ' + zipcode,
          error,
        })
        throw error.response
      })
  }

  async getGeneralConfigJson(): Promise<HubConfig> {
    return this.http
      .get(`${this.url}/hub/configurations`)
      .then((r: AxiosResponse) => r.data || { maintenance: false })
      .catch((error: any) => {
        this.logServce.error({
          message: 'Error on get general config',
          error,
        })
        return { maintenance: false }
      })
  }
}
